import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { API_URL, APP_VERSION } from "./config/app";
import "./index.css";

const ACCESS_TOKEN_COOKIE = "dalibith_field_access_token";
const ONE_WEEK_IN_SECONDS = 7 * 24 * 60 * 60;
const actions = [
  { label: "Horas", title: "Reportar horas", icon: "clock", key: "hours" },
  { label: "Tareas", title: "Reportar tareas", icon: "helmet", key: "tasks" },
  { label: "Solicitar", title: "Solicitar material", icon: "up", key: "materials" },
  { label: "Recepción", title: "Recepción de material", icon: "down", key: "receipts" }
];

function getCookie(name) {
  const prefix = `${name}=`;
  return document.cookie.split(";").map((cookie) => cookie.trim()).find((cookie) => cookie.startsWith(prefix))?.slice(prefix.length);
}

function deleteAccessToken() {
  document.cookie = `${ACCESS_TOKEN_COOKIE}=; Max-Age=0; Path=/; SameSite=Strict`;
}

function storeAccessToken(token) {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${ACCESS_TOKEN_COOKIE}=${encodeURIComponent(token)}; Max-Age=${ONE_WEEK_IN_SECONDS}; Path=/; SameSite=Strict${secure}`;
}

function decodeJwt(token) {
  try {
    const payload = token.split(".")[1];
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(atob(base64).split("").map((character) => `%${`00${character.charCodeAt(0).toString(16)}`.slice(-2)}`).join(""));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getValidSession(token) {
  const claims = decodeJwt(token);
  if (!claims || !claims.exp || claims.exp * 1000 <= Date.now() || claims.scope !== "field") return null;
  if (!claims.operarioId) return null;
  return { token, nombre: claims.operarioNombre, operarioId: claims.operarioId };
}

function getAccessCodeFromUrl() {
  const { pathname, searchParams } = new URL(window.location.href);
  const tokenFromQuery = searchParams.get("token");
  const accessPath = pathname.match(/^\/acceso\/([^/]+)\/?$/);
  return tokenFromQuery || (accessPath ? accessPath[1] : null);
}

function formatDateForInput(date) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

function UnauthenticatedScreen() {
  return <main className="unauthenticated-screen"><img src="/media/logos/favicon.svg" alt="NEO4" /></main>;
}

function Modal({ children, sidePanel = false, card = false, closing = false }) {
  return <div className={`field-modal-backdrop${sidePanel ? " field-modal-backdrop-side" : ""}${card ? " field-modal-backdrop-card" : ""}${closing ? " field-modal-backdrop-closing" : ""}`}>{children}</div>;
}

function FieldIcon({ name }) {
  const svgProps = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.9", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true };
  if (name === "clock") return <svg {...svgProps}><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5v5l3.25 2" /></svg>;
  if (name === "helmet") return <svg {...svgProps}><path d="M4 14.5h16" /><path d="M6.25 14.5v-1.25a5.75 5.75 0 0 1 11.5 0v1.25" /><path d="M12 7.5v-2" /><path d="M3.5 17h17" /></svg>;
  if (name === "mic") return <svg {...svgProps}><rect x="8.5" y="3.5" width="7" height="11" rx="3.5" /><path d="M6 11.5a6 6 0 0 0 12 0" /><path d="M12 17.5V21" /><path d="M8.5 21h7" /></svg>;
  if (name === "up") return <svg {...svgProps}><path d="M12 19V5" /><path d="m6.5 10.5 5.5-5.5 5.5 5.5" /></svg>;
  return <svg {...svgProps}><path d="M12 5v14" /><path d="m6.5 13.5 5.5 5.5 5.5-5.5" /></svg>;
}

function TaskVoiceActivity({ sending = false }) {
  const title = sending ? "Enviando grabación" : "Grabando audio";
  const description = sending
    ? "Estamos transcribiendo el audio y preparando las tareas propuestas."
    : "Explica qué has realizado, cantidades y compañeros que han participado.";

  return <section className="task-voice-activity" aria-live="polite" aria-label={title}>
    <span className="task-voice-spinner" aria-hidden="true" />
    <div>
      <strong>{title}</strong>
      <p>{description}</p>
    </div>
    <div className="task-voice-wave" aria-hidden="true"><span /><span /><span /><span /><span /><span /><span /></div>
  </section>;
}

function getAudioRecordingFormat(recorder, chunks) {
  const mimeType = recorder.mimeType || chunks.find((chunk) => chunk.type)?.type || "audio/webm";
  const normalizedMimeType = mimeType.split(";")[0].trim().toLowerCase();
  const extension = normalizedMimeType.includes("mp4") || normalizedMimeType.includes("m4a") || normalizedMimeType.includes("aac")
    ? "mp4"
    : normalizedMimeType.includes("mpeg") || normalizedMimeType.includes("mp3")
      ? "mp3"
      : normalizedMimeType.includes("wav")
        ? "wav"
        : "webm";
  return { mimeType, extension };
}

function App() {
  const [session, setSession] = useState(null);
  const [hoursScreen, setHoursScreen] = useState("home");
  const [hoursViewClosing, setHoursViewClosing] = useState(false);
  const [obraPickerOpen, setObraPickerOpen] = useState(false);
  const [obras, setObras] = useState([]);
  const [selectedObra, setSelectedObra] = useState(null);
  const [reportingOperario, setReportingOperario] = useState(null);
  const [operarioPickerOpen, setOperarioPickerOpen] = useState(false);
  const [sidePanelClosing, setSidePanelClosing] = useState("");
  const [hoursEntry, setHoursEntry] = useState({ fecha: formatDateForInput(new Date()), horas: 0, minutos: 0, observaciones: "" });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [notification, setNotification] = useState("");
  const [lastHoursReport, setLastHoursReport] = useState(null);
  const [materialScreen, setMaterialScreen] = useState("home");
  const [materialViewClosing, setMaterialViewClosing] = useState(false);
  const [materialObraPickerOpen, setMaterialObraPickerOpen] = useState(false);
  const [materialObra, setMaterialObra] = useState(null);
  const [materialEditor, setMaterialEditor] = useState({ capitulos: [] });
  const [materialSelections, setMaterialSelections] = useState([]);
  const [materialLoading, setMaterialLoading] = useState(false);
  const [materialSaving, setMaterialSaving] = useState(false);
  const [materialFeedback, setMaterialFeedback] = useState("");
  const [expandedChapters, setExpandedChapters] = useState([]);
  const [expandedPartidas, setExpandedPartidas] = useState([]);
  const [extraMaterialOpen, setExtraMaterialOpen] = useState(false);
  const [extraSearch, setExtraSearch] = useState("");
  const [extraProducts, setExtraProducts] = useState([]);
  const [taskScreen, setTaskScreen] = useState("home");
  const [taskViewClosing, setTaskViewClosing] = useState(false);
  const [taskObraPickerOpen, setTaskObraPickerOpen] = useState(false);
  const [taskObra, setTaskObra] = useState(null);
  const [taskEditor, setTaskEditor] = useState(null);
  const [taskLoading, setTaskLoading] = useState(false);
  const [taskSaving, setTaskSaving] = useState(false);
  const [taskFeedback, setTaskFeedback] = useState("");
  const [expandedTaskChapters, setExpandedTaskChapters] = useState([]);
  const [expandedTaskPartidas, setExpandedTaskPartidas] = useState([]);
  const [taskLineDraft, setTaskLineDraft] = useState(null);
  const [taskVoiceOpen, setTaskVoiceOpen] = useState(false);
  const [taskVoiceClosing, setTaskVoiceClosing] = useState(false);
  const [taskVoiceRecording, setTaskVoiceRecording] = useState(false);
  const [taskVoiceFiles, setTaskVoiceFiles] = useState([]);
  const [taskVoiceSending, setTaskVoiceSending] = useState(false);
  const [taskVoiceError, setTaskVoiceError] = useState("");
  const taskVoiceRecorderRef = useRef(null);
  const [receiptScreen, setReceiptScreen] = useState("home");
  const [receiptViewClosing, setReceiptViewClosing] = useState(false);
  const [receiptObraPickerOpen, setReceiptObraPickerOpen] = useState(false);
  const [receiptObra, setReceiptObra] = useState(null);
  const [receiptOrders, setReceiptOrders] = useState([]);
  const [receiptEditor, setReceiptEditor] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptSaving, setReceiptSaving] = useState(false);
  const [receiptAnalyzing, setReceiptAnalyzing] = useState(false);
  const [receiptFeedback, setReceiptFeedback] = useState("");
  const [receiptFiles, setReceiptFiles] = useState([]);
  const [cameraOpen, setCameraOpen] = useState(false);
  const receiptCameraVideoRef = useRef(null);
  const receiptCameraStreamRef = useRef(null);

  useEffect(() => {
    let active = true;
    const accessCode = getAccessCodeFromUrl();
    async function authenticate() {
      if (!accessCode) {
        const storedSession = getValidSession(decodeURIComponent(getCookie(ACCESS_TOKEN_COOKIE) || ""));
        if (storedSession && active) setSession(storedSession);
        if (!storedSession) deleteAccessToken();
        return;
      }
      try {
        if (!API_URL) throw new Error("La URL de la API no está configurada");
        const response = await fetch(`${API_URL}/login/operario`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token: accessCode }) });
        const result = response.ok ? await response.json() : null;
        const newSession = result?.accessToken ? getValidSession(result.accessToken) : null;
        if (!newSession) throw new Error("Código de acceso no válido");
        storeAccessToken(newSession.token);
        if (active) setSession({ ...newSession, nombre: result.operario?.nombre || newSession.nombre, operarioId: result.operario?.id || newSession.operarioId });
      } catch {
        deleteAccessToken();
      } finally {
        window.history.replaceState({}, document.title, "/");
      }
    }
    authenticate();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!notification) return undefined;
    const timeout = window.setTimeout(() => setNotification(""), 4000);
    return () => window.clearTimeout(timeout);
  }, [notification]);

  useEffect(() => {
    const video = receiptCameraVideoRef.current;
    const stream = receiptCameraStreamRef.current;
    if (!cameraOpen || !video || !stream) return undefined;
    video.srcObject = stream;
    video.play().catch(() => {});
    return undefined;
  }, [cameraOpen]);

  useEffect(() => () => {
    const stream = receiptCameraStreamRef.current;
    if (stream) stream.getTracks().forEach((track) => track.stop());
  }, []);

  async function fieldRequest(path, options = {}) {
    if (!API_URL || !session?.token) throw new Error("Sesión no disponible");
    const response = await fetch(`${API_URL}${path}`, {
      ...options,
      headers: { Authorization: `Bearer ${session.token}`, ...(options.body && !(options.body instanceof FormData) ? { "Content-Type": "application/json" } : {}), ...(options.headers || {}) }
    });
    if (!response.ok) throw new Error("No se ha podido completar la operación");
    return response.status === 204 ? null : response.json();
  }

  async function loadHoursObras() {
    setLoading(true);
    try {
      setObras(await fieldRequest("/obra-headers"));
    } catch {
      setFeedback("No se han podido cargar tus obras.");
    } finally {
      setLoading(false);
    }
  }

  function resetHoursEntry() {
    setHoursEntry({ fecha: formatDateForInput(new Date()), horas: 0, minutos: 0, observaciones: "" });
  }

  async function openHoursReport() {
    setHoursViewClosing(false);
    setHoursScreen("report");
    setSelectedObra(null);
    setReportingOperario({ id: session.operarioId, nombre: session.nombre });
    resetHoursEntry();
    setFeedback("");
    await loadHoursObras();
  }

  function closeHoursReport() {
    if (hoursViewClosing) return;

    setObraPickerOpen(false);
    setOperarioPickerOpen(false);
    setSidePanelClosing("");
    setHoursViewClosing(true);

    window.setTimeout(() => {
      setHoursScreen("home");
      setHoursViewClosing(false);
      setFeedback("");
    }, 240);
  }

  function closeSidePanel(panel) {
    setSidePanelClosing(panel);
    window.setTimeout(() => {
      if (panel === "obra") setObraPickerOpen(false);
      if (panel === "operario") setOperarioPickerOpen(false);
      if (panel === "material-obra") setMaterialObraPickerOpen(false);
      if (panel === "extra-material") setExtraMaterialOpen(false);
      if (panel === "receipt-obra") setReceiptObraPickerOpen(false);
      if (panel === "task-obra") setTaskObraPickerOpen(false);
      setSidePanelClosing("");
    }, 260);
  }

  async function openObraPicker() {
    setObraPickerOpen(true);
    setFeedback("");
    setSidePanelClosing("");
    if (!obras.length) await loadHoursObras();
  }

  async function openOperarioPicker() {
    if (!selectedObra) return;
    setOperarioPickerOpen(true);
    setFeedback("");
    setSidePanelClosing("");
  }

  async function loadHours(obra, fecha, operarioId = reportingOperario?.id) {
    if (!operarioId) return;
    setLoading(true);
    setFeedback("");
    try {
      const rows = await fieldRequest(`/obra-headers/${obra.id}/operarios/${operarioId}/hours`);
      const jornada = rows.find((row) => formatDateForInput(new Date(row.fecha)) === fecha);
      setHoursEntry({ fecha, horas: Number(jornada?.horas || 0), minutos: Number(jornada?.minutos || 0), observaciones: jornada?.observaciones || "" });
    } catch { setFeedback("No se ha podido cargar el parte diario."); } finally { setLoading(false); }
  }

  function selectObra(obra) {
    setSelectedObra(obra);
    closeSidePanel("obra");
    const currentOperario = { id: session.operarioId, nombre: session.nombre };
    setReportingOperario(currentOperario);
    resetHoursEntry();
    setFeedback("");
    loadHours(obra, formatDateForInput(new Date()), currentOperario.id);
  }

  function selectReportingOperario(operario) {
    setReportingOperario(operario);
    closeSidePanel("operario");
    resetHoursEntry();
    if (!selectedObra) return;
    setFeedback("");
    loadHours(selectedObra, formatDateForInput(new Date()), operario.id);
  }

  function adjustHours(minutes) {
    setHoursEntry((current) => {
      const total = Math.max(0, current.horas * 60 + current.minutos + minutes);
      return { ...current, horas: Math.floor(total / 60), minutos: total % 60 };
    });
  }

  function setHoursUnit(unit, value) {
    const numeric = Math.max(0, Number.parseInt(String(value).replace(/\D/g, ""), 10) || 0);
    setHoursEntry((current) => {
      const total = (unit === "horas" ? numeric : current.horas) * 60 + (unit === "minutos" ? numeric : current.minutos);
      return { ...current, horas: Math.floor(total / 60), minutos: total % 60 };
    });
  }

  async function changeHoursDate(fecha) {
    setHoursEntry({ fecha, horas: 0, minutos: 0, observaciones: "" });
    if (selectedObra && reportingOperario && fecha) await loadHours(selectedObra, fecha, reportingOperario.id);
  }

  async function saveHours() {
    if (!selectedObra) { setFeedback("Selecciona una obra antes de guardar."); return; }
    if (!reportingOperario) { setFeedback("Selecciona el operario al que corresponden las horas."); return; }
    if (hoursEntry.horas * 60 + hoursEntry.minutos <= 0) { setFeedback("Indica una duración mayor que cero."); return; }
    setSaving(true);
    setFeedback("");
    try {
      await fieldRequest(`/obra-headers/${selectedObra.id}/operarios/${reportingOperario.id}/hours`, { method: "POST", body: JSON.stringify(hoursEntry) });
      const totalMinutes = hoursEntry.horas * 60 + hoursEntry.minutos;
      setLastHoursReport({ obra: selectedObra, fecha: hoursEntry.fecha, totalMinutes, operario: reportingOperario });
      setNotification(`Horas de ${reportingOperario.nombre} actualizadas.`);
      closeHoursReport();
    } catch { setFeedback("No se han podido guardar las horas."); } finally { setSaving(false); }
  }

  function openMaterialRequest() {
    setMaterialViewClosing(false);
    setMaterialScreen("request");
    setMaterialObra(null);
    setMaterialEditor({ capitulos: [] });
    setMaterialSelections([]);
    setMaterialFeedback("");
    setExpandedChapters([]);
    setExpandedPartidas([]);
  }

  function closeMaterialRequest() {
    if (materialViewClosing) return;

    setMaterialObraPickerOpen(false);
    setExtraMaterialOpen(false);
    setSidePanelClosing("");
    setMaterialViewClosing(true);

    window.setTimeout(() => {
      setMaterialScreen("home");
      setMaterialViewClosing(false);
      setMaterialFeedback("");
    }, 240);
  }

  async function openMaterialObraPicker() {
    setMaterialObraPickerOpen(true);
    setSidePanelClosing("");
    setMaterialFeedback("");
    setMaterialLoading(true);
    try { setObras(await fieldRequest("/obra-headers")); } catch { setMaterialFeedback("No se han podido cargar tus obras."); } finally { setMaterialLoading(false); }
  }

  async function selectMaterialObra(obra) {
    setMaterialObra(obra);
    closeSidePanel("material-obra");
    setMaterialLoading(true);
    setMaterialFeedback("");
    try {
      const editor = await fieldRequest(`/obra-headers/${obra.id}/material-requests/editor`);
      setMaterialEditor(editor);
    } catch { setMaterialFeedback("No se han podido cargar los materiales de esta obra."); } finally { setMaterialLoading(false); }
  }

  function materialKey(line) {
    return line.es_material_extra ? `extra-${line.plantilla_producto_original_id}` : `obra-${line.obra_producto_id}`;
  }

  function updateMaterialSelection(line, quantity) {
    const normalizedQuantity = Math.max(0, Number(String(quantity).replace(",", ".")) || 0);
    setMaterialSelections((current) => {
      const key = materialKey(line);
      const existing = current.find((item) => materialKey(item) === key);
      if (normalizedQuantity <= 0) return current.filter((item) => materialKey(item) !== key);
      return existing ? current.map((item) => materialKey(item) === key ? { ...item, cantidad_solicitada: normalizedQuantity } : item) : [...current, { ...line, cantidad_solicitada: normalizedQuantity }];
    });
  }

  function addPlannedMaterial(chapter, partida, product) {
    const line = { es_material_extra: false, capitulo_id: chapter.capitulo_id, partida_id: partida.partida_id, obra_producto_id: product.obra_producto_id, plantilla_producto_original_id: product.plantilla_producto_original_id, descripcion: product.descripcion, referencia: product.referencia, unidad: product.unidad, cantidad_pendiente: Number(product.cantidad_pendiente) };
    const selected = materialSelections.find((item) => materialKey(item) === materialKey(line));
    updateMaterialSelection(line, selected ? selected.cantidad_solicitada + 1 : 1);
  }

  function addPartidaMaterials(chapter, partida) {
    (partida.productos || []).forEach((product) => addPlannedMaterial(chapter, partida, product));
  }

  async function searchExtraMaterials(value) {
    setExtraSearch(value);
    if (value.trim().length < 2) { setExtraProducts([]); return; }
    try { setExtraProducts(await fieldRequest(`/products?query=${encodeURIComponent(value.trim())}`)); } catch { setMaterialFeedback("No se han podido buscar materiales adicionales."); }
  }

  function addExtraMaterial(product) {
    const line = { es_material_extra: true, plantilla_producto_original_id: product.id, descripcion: product.descripcion, referencia: product.referencia, unidad: product.tipo_unidad?.descripcion || "" };
    const selected = materialSelections.find((item) => materialKey(item) === materialKey(line));
    updateMaterialSelection(line, selected ? selected.cantidad_solicitada + 1 : 1);
    closeSidePanel("extra-material");
    window.setTimeout(() => {
      setExtraSearch("");
      setExtraProducts([]);
    }, 260);
  }

  async function saveMaterialRequest() {
    if (!materialObra) { setMaterialFeedback("Selecciona una obra antes de guardar."); return; }
    if (!materialSelections.length) { setMaterialFeedback("Añade al menos un material."); return; }
    setMaterialSaving(true);
    setMaterialFeedback("");
    try {
      const response = await fieldRequest(`/obra-headers/${materialObra.id}/material-requests`, { method: "POST", body: JSON.stringify({ observaciones: materialEditor.observaciones || null, lineas: materialSelections.map(({ capitulo_id, partida_id, obra_producto_id, plantilla_producto_original_id, es_material_extra, cantidad_solicitada }) => ({ capitulo_id, partida_id, obra_producto_id, plantilla_producto_original_id, es_material_extra, cantidad_solicitada })) }) });
      setNotification(`Solicitud ${response.codigo || "de material"} registrada.`);
      closeMaterialRequest();
    } catch { setMaterialFeedback("No se ha podido guardar la solicitud de material."); } finally { setMaterialSaving(false); }
  }

  function openTaskReport() {
    setTaskViewClosing(false);
    setTaskScreen("report");
    setTaskObra(null);
    setTaskEditor(null);
    setTaskFeedback("");
    setExpandedTaskChapters([]);
    setExpandedTaskPartidas([]);
  }

  function stopTaskVoiceRecording(discard = false) {
    const recorder = taskVoiceRecorderRef.current;
    taskVoiceRecorderRef.current = null;
    if (!recorder) return;
    if (discard) recorder.onstop = null;
    if (recorder.state !== "inactive") recorder.stop();
    if (recorder.stream) recorder.stream.getTracks().forEach((track) => track.stop());
    setTaskVoiceRecording(false);
  }

  function closeTaskReport() {
    if (taskViewClosing) return;
    stopTaskVoiceRecording(true);
    setTaskObraPickerOpen(false);
    setTaskLineDraft(null);
    setTaskVoiceOpen(false);
    setSidePanelClosing("");
    setTaskViewClosing(true);

    window.setTimeout(() => {
      setTaskScreen("home");
      setTaskViewClosing(false);
      setTaskFeedback("");
    }, 240);
  }

  async function openTaskObraPicker() {
    setTaskObraPickerOpen(true);
    setSidePanelClosing("");
    setTaskFeedback("");
    setTaskLoading(true);
    try { setObras(await fieldRequest("/obra-headers")); } catch { setTaskFeedback("No se han podido cargar tus obras."); } finally { setTaskLoading(false); }
  }

  async function selectTaskObra(obra) {
    setTaskObra(obra);
    closeSidePanel("task-obra");
    setTaskLoading(true);
    setTaskFeedback("");
    try {
      const editor = await fieldRequest(`/obra-headers/${obra.id}/operarios/${session.operarioId}/task-report/editor`);
      setTaskEditor({ fecha: formatDateForInput(new Date()), capitulos: editor.capitulos || [], lineas: [], ai_resultado: null });
      setExpandedTaskChapters([]);
      setExpandedTaskPartidas([]);
    } catch { setTaskFeedback("No se ha podido abrir el parte de tareas."); } finally { setTaskLoading(false); }
  }

  function openTaskLine(chapter, partida, product, allAvailable) {
    const selected = (taskEditor?.lineas || []).find((line) => line.producto_id === product.producto_id);
    setTaskLineDraft({
      chapter,
      partida,
      product,
      cantidad: selected?.cantidad ?? (allAvailable ? Number(product.cantidad_disponible_para_reportar || 0) : ""),
      companero_ids: selected?.companero_ids || []
    });
  }

  function saveTaskLine() {
    const draft = taskLineDraft;
    const quantity = Number(String(draft?.cantidad || "").replace(",", "."));
    const available = Number(draft?.product?.cantidad_disponible_para_reportar || 0);
    if (!draft || !Number.isFinite(quantity) || quantity <= 0 || quantity > available) {
      setTaskFeedback("Indica una cantidad válida, dentro del material recibido disponible.");
      return;
    }
    setTaskEditor((current) => ({
      ...current,
      lineas: [
        ...(current.lineas || []).filter((line) => line.producto_id !== draft.product.producto_id),
        { capitulo_id: draft.chapter.capitulo_id, partida_id: draft.partida.partida_id, producto_id: draft.product.producto_id, descripcion: draft.product.descripcion, cantidad: quantity, companero_ids: draft.companero_ids || [] }
      ]
    }));
    setTaskLineDraft(null);
  }

  async function saveTaskReport() {
    if (!(taskEditor?.lineas || []).length) { setTaskFeedback("Añade al menos una tarea al parte antes de enviarlo."); return; }
    setTaskSaving(true);
    setTaskFeedback("");
    try {
      await fieldRequest(`/obra-headers/${taskObra.id}/operarios/${session.operarioId}/task-reports`, {
        method: "POST",
        body: JSON.stringify({ fecha: taskEditor.fecha, lineas: taskEditor.lineas.map((line) => ({ capitulo_id: line.capitulo_id, partida_id: line.partida_id, producto_id: line.producto_id, cantidad: Number(line.cantidad), companero_ids: line.companero_ids || [] })) })
      });
      setNotification("Parte de tareas registrado.");
      closeTaskReport();
    } catch { setTaskFeedback("No se ha podido enviar el parte de tareas."); } finally { setTaskSaving(false); }
  }

  function openTaskVoice() {
    setTaskVoiceFiles([]);
    setTaskVoiceError("");
    setTaskVoiceClosing(false);
    setTaskVoiceOpen(true);
  }

  async function startTaskVoiceRecording() {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setTaskVoiceError("Este navegador no permite grabar audio.");
      return;
    }
    setTaskVoiceError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chunks = [];
      const recorder = new MediaRecorder(stream);
      recorder.ondataavailable = (event) => { if (event.data?.size) chunks.push(event.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        taskVoiceRecorderRef.current = null;
        const format = getAudioRecordingFormat(recorder, chunks);
        const recording = chunks.length ? new File([new Blob(chunks, { type: format.mimeType })], `parte-${Date.now()}.${format.extension}`, { type: format.mimeType }) : null;
        if (recording) setTaskVoiceFiles((current) => [...current, recording]);
        else setTaskVoiceError("No se ha podido guardar la grabación.");
        setTaskVoiceRecording(false);
      };
      taskVoiceRecorderRef.current = recorder;
      recorder.start();
      setTaskVoiceRecording(true);
    } catch { setTaskVoiceError("No se ha podido acceder al micrófono."); }
  }

  function closeTaskVoice() {
    if (taskVoiceClosing) return;
    stopTaskVoiceRecording(true);
    setTaskVoiceClosing(true);
    window.setTimeout(() => {
      setTaskVoiceOpen(false);
      setTaskVoiceClosing(false);
      setTaskVoiceFiles([]);
      setTaskVoiceError("");
    }, 260);
  }

  async function sendTaskVoice() {
    if (!taskVoiceFiles.length || !taskEditor || !taskObra) return;
    setTaskVoiceSending(true);
    setTaskVoiceError("");
    try {
      const formData = new FormData();
      taskVoiceFiles.forEach((file) => formData.append("files", file));
      const result = await fieldRequest(`/obra-headers/${taskObra.id}/operarios/${session.operarioId}/task-assistant/audio`, { method: "POST", body: formData });
      const products = (taskEditor.capitulos || []).flatMap((chapter) => (chapter.partidas || []).flatMap((partida) => (partida.productos || []).map((product) => ({ chapter, partida, product }))));
      const lines = (result.tareas_propuestas || []).map((proposal) => {
        const match = products.find((item) => item.product.producto_id === proposal.producto_id);
        return match ? { capitulo_id: proposal.capitulo_id, partida_id: proposal.partida_id, producto_id: proposal.producto_id, descripcion: match.product.descripcion, cantidad: Number(proposal.cantidad), companero_ids: (proposal.operario_ids || []).filter((id) => id !== session.operarioId) } : null;
      }).filter(Boolean);
      setTaskEditor((current) => ({ ...current, lineas: [...(current.lineas || []).filter((line) => !lines.some((next) => next.producto_id === line.producto_id)), ...lines], ai_resultado: result }));
      closeTaskVoice();
    } catch { setTaskVoiceError("No se ha podido interpretar la grabación."); } finally { setTaskVoiceSending(false); }
  }

  function clearReceiptFiles() {
    setReceiptFiles((current) => { current.forEach((item) => URL.revokeObjectURL(item.preview)); return []; });
  }

  function openReceipt() {
    setReceiptViewClosing(false);
    setReceiptScreen("receipt");
    setReceiptObra(null);
    setReceiptOrders([]);
    setReceiptEditor(null);
    setReceiptFeedback("");
    clearReceiptFiles();
  }

  function closeReceipt() {
    if (receiptViewClosing) return;

    stopReceiptCamera();
    setReceiptObraPickerOpen(false);
    setSidePanelClosing("");
    setReceiptViewClosing(true);
    clearReceiptFiles();

    window.setTimeout(() => {
      setReceiptScreen("home");
      setReceiptViewClosing(false);
      setReceiptFeedback("");
    }, 240);
  }

  async function openReceiptObraPicker() {
    setReceiptObraPickerOpen(true);
    setSidePanelClosing("");
    setReceiptFeedback("");
    setReceiptLoading(true);
    try { setObras(await fieldRequest("/obra-headers")); } catch { setReceiptFeedback("No se han podido cargar tus obras."); } finally { setReceiptLoading(false); }
  }

  async function selectReceiptObra(obra, closePicker = true) {
    setReceiptObra(obra);
    if (closePicker) closeSidePanel("receipt-obra");
    setReceiptLoading(true);
    setReceiptFeedback("");
    try { setReceiptOrders(await fieldRequest(`/obra-headers/${obra.id}/purchase-orders`)); } catch { setReceiptFeedback("No se han podido cargar los pedidos de esta obra."); } finally { setReceiptLoading(false); }
  }

  async function openReceiptEditor(order) {
    setReceiptLoading(true);
    setReceiptFeedback("");
    clearReceiptFiles();
    try {
      const editor = await fieldRequest(`/obra-headers/${receiptObra.id}/purchase-orders/${order.id}/receipt-editor`);
      setReceiptEditor({ order: editor, albaran_proveedor: "", materiales_no_identificados: [], lineas: (editor.lineas || []).map((line) => ({ ...line, cantidad_actual: 0 })) });
    } catch { setReceiptFeedback("No se ha podido abrir la recepción del pedido."); } finally { setReceiptLoading(false); }
  }

  function addReceiptFiles(fileList) {
    const selected = Array.from(fileList || []);
    const allowed = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!selected.length) return;
    if (selected.some((file) => !allowed.includes(file.type) && !/\.(jpe?g|png|webp|pdf)$/i.test(file.name))) { setReceiptFeedback("Solo puedes añadir fotos JPG, PNG, WebP o archivos PDF."); return; }
    setReceiptFiles((current) => {
      const next = [...current, ...selected.map((file) => ({ file, preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : "" }))];
      if (next.length > 10) { next.slice(current.length).forEach((item) => URL.revokeObjectURL(item.preview)); setReceiptFeedback("Puedes añadir un máximo de 10 archivos."); return current; }
      if (next.reduce((total, item) => total + item.file.size, 0) > 25 * 1024 * 1024) { next.slice(current.length).forEach((item) => URL.revokeObjectURL(item.preview)); setReceiptFeedback("El conjunto de fotos y PDFs no puede superar 25 MB."); return current; }
      setReceiptFeedback("");
      return next;
    });
  }

  async function startReceiptCamera() {
    if (!navigator.mediaDevices?.getUserMedia) {
      setReceiptFeedback("Este navegador no permite acceder a la cámara.");
      return;
    }

    setReceiptFeedback("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" } }
      });
      receiptCameraStreamRef.current = stream;
      setCameraOpen(true);
    } catch {
      setReceiptFeedback("No se ha podido acceder a la cámara. Revisa el permiso del navegador.");
    }
  }

  function stopReceiptCamera() {
    const stream = receiptCameraStreamRef.current;
    if (stream) stream.getTracks().forEach((track) => track.stop());
    receiptCameraStreamRef.current = null;
    if (receiptCameraVideoRef.current) receiptCameraVideoRef.current.srcObject = null;
    setCameraOpen(false);
  }

  function takeReceiptPhoto() {
    const video = receiptCameraVideoRef.current;
    if (!video?.videoWidth || !video.videoHeight) {
      setReceiptFeedback("La cámara todavía no está preparada. Inténtalo de nuevo.");
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) {
        setReceiptFeedback("No se ha podido tomar la foto.");
        return;
      }
      addReceiptFiles([new File([blob], `albaran-${Date.now()}.jpg`, { type: "image/jpeg" })]);
      stopReceiptCamera();
    }, "image/jpeg", 0.9);
  }

  function removeReceiptFile(index) {
    setReceiptFiles((current) => { const item = current[index]; if (item) URL.revokeObjectURL(item.preview); return current.filter((_, itemIndex) => itemIndex !== index); });
  }

  async function analyzeReceiptFiles() {
    if (!receiptEditor || !receiptFiles.length) { setReceiptFeedback("Haz al menos una foto del albarán."); return; }
    setReceiptAnalyzing(true);
    setReceiptFeedback("");
    try {
      const formData = new FormData();
      receiptFiles.forEach((item) => formData.append("files", item.file));
      const result = await fieldRequest(`/obra-headers/${receiptObra.id}/purchase-orders/${receiptEditor.order.id}/delivery-note/analyze`, { method: "POST", body: formData });
      const quantities = new Map((result.lineas || []).map((line) => [line.pedido_obra_linea_id, Number(line.cantidad_recibida || 0)]));
      setReceiptEditor((current) => ({ ...current, albaran_proveedor: result.albaran_proveedor || current.albaran_proveedor, materiales_no_identificados: result.materiales_no_identificados || [], lineas: current.lineas.map((line) => quantities.has(line.id) ? { ...line, cantidad_actual: quantities.get(line.id) } : line) }));
      clearReceiptFiles();
      setNotification("Albarán analizado. Revisa las cantidades antes de guardar.");
    } catch { setReceiptFeedback("No se han podido analizar las fotos del albarán."); } finally { setReceiptAnalyzing(false); }
  }

  function updateReceiptQuantity(lineId, quantity) {
    const value = Math.max(0, Number(String(quantity).replace(",", ".")) || 0);
    setReceiptEditor((current) => ({ ...current, lineas: current.lineas.map((line) => line.id === lineId ? { ...line, cantidad_actual: value } : line) }));
  }

  async function saveReceipt() {
    if (!receiptEditor || !receiptEditor.lineas.some((line) => Number(line.cantidad_actual) > 0)) { setReceiptFeedback("Indica al menos un material recibido."); return; }
    setReceiptSaving(true);
    setReceiptFeedback("");
    try {
      await fieldRequest(`/obra-headers/${receiptObra.id}/purchase-orders/${receiptEditor.order.id}/receipts`, { method: "POST", body: JSON.stringify({ operario_id: session.operarioId, albaran_proveedor: receiptEditor.albaran_proveedor, lineas: receiptEditor.lineas.map((line) => ({ pedido_obra_linea_id: line.id, cantidad_recibida: Number(line.cantidad_actual || 0) })) }) });
      setNotification("Recepción de material registrada.");
      setReceiptEditor(null);
      await selectReceiptObra(receiptObra, false);
    } catch { setReceiptFeedback("No se ha podido guardar la recepción."); } finally { setReceiptSaving(false); }
  }

  if (!session) return <UnauthenticatedScreen />;
  const formatDuration = (totalMinutes) => `${Math.floor(totalMinutes / 60)} h ${String(totalMinutes % 60).padStart(2, "0")} min`;
  const operariosAsignadosAObra = selectedObra?.operarios_externos || [];
  const currentOperarioIsExternal = operariosAsignadosAObra.some((operario) => operario.id === session.operarioId);
  const operariosDisponiblesParaReporte = currentOperarioIsExternal
    ? [{ id: session.operarioId, nombre: session.nombre }]
    : [{ id: session.operarioId, nombre: session.nombre }, ...operariosAsignadosAObra.filter((operario) => operario.id !== session.operarioId)];

  if (taskScreen === "report") {
    const taskCompanions = (taskObra?.operarios_externos || []).filter((operario) => operario.id !== session.operarioId);
    return (
      <main className="field-app home-screen field-screen">
        <header className="home-user-bar"><strong>{session.nombre}</strong></header>
        <section className={`field-workspace task-workspace ${taskViewClosing ? "field-view-exit" : "field-view-enter"}`}>
          <header className="field-view-header"><button className="view-control-button" type="button" onClick={closeTaskReport} aria-label="Volver">←</button><p>REPORTAR TAREAS</p><button className="view-control-button" type="button" onClick={closeTaskReport} aria-label="Cerrar">×</button></header>
          <button className="selected-obra-button" type="button" onClick={openTaskObraPicker}><span>OBRA</span>{taskObra ? <><strong>{taskObra.codigo_obra || "Obra"}</strong><small>{taskObra.oferta_referencia || taskObra.localizacion || "Sin referencia"}</small></> : <strong className="placeholder">Selecciona una obra</strong>}<b aria-hidden="true">→</b></button>
          {taskObra && <section className="task-content-panel">{taskLoading && <p className="modal-message">Cargando tareas…</p>}{taskEditor && <><label className="field-label">Fecha<input type="date" value={taskEditor.fecha} onChange={(event) => setTaskEditor((current) => ({ ...current, fecha: event.target.value }))} /></label><div className="task-section-title">Capítulos, partidas y tareas</div><div className="task-tree">{taskEditor.capitulos.map((chapter) => { const activePartidas = (chapter.partidas || []).filter((partida) => (partida.productos || []).some((product) => product.puede_reportar)); const enabled = activePartidas.length > 0; const chapterOpen = expandedTaskChapters.includes(chapter.capitulo_id); return <section className={`task-chapter${enabled ? "" : " task-chapter-disabled"}`} key={chapter.capitulo_id}><button type="button" disabled={!enabled} className="task-chapter-button" onClick={() => setExpandedTaskChapters((current) => current.includes(chapter.capitulo_id) ? current.filter((id) => id !== chapter.capitulo_id) : [...current, chapter.capitulo_id])}><span>CAP. {chapter.ordinal}</span><strong>{chapter.descripcion}</strong><b>{chapterOpen ? "−" : "+"}</b></button>{chapterOpen && activePartidas.map((partida) => { const partidaOpen = expandedTaskPartidas.includes(partida.partida_id); return <section className="task-partida" key={partida.partida_id}><button type="button" className="task-partida-button" onClick={() => setExpandedTaskPartidas((current) => current.includes(partida.partida_id) ? current.filter((id) => id !== partida.partida_id) : [...current, partida.partida_id])}><strong>{partida.ordinal}. {partida.descripcion}</strong><b>{partidaOpen ? "−" : "+"}</b></button>{partidaOpen && (partida.productos || []).map((product) => { const selected = taskEditor.lineas.find((line) => line.producto_id === product.producto_id); return <article className={`task-product${product.puede_reportar ? "" : " task-product-disabled"}`} key={product.producto_id}><strong>{product.descripcion}</strong><small>Pendiente: {product.cantidad_pendiente} · Disponible: {product.cantidad_disponible_para_reportar}</small>{selected && <span className="task-product-selected">Preparada: {selected.cantidad}</span>}<div><button type="button" disabled={!product.puede_reportar} className="mini-add-button" onClick={() => openTaskLine(chapter, partida, product, true)}>Todo</button><button type="button" disabled={!product.puede_reportar} className="secondary-button" onClick={() => openTaskLine(chapter, partida, product, false)}>{selected ? "Modificar" : "Parcial"}</button></div></article>; })}</section>; })}</section>; })}</div><section className="task-selected-lines"><strong>Tareas preparadas ({taskEditor.lineas.length})</strong>{taskEditor.lineas.length ? taskEditor.lineas.map((line) => <div key={line.producto_id}><div><strong>{line.descripcion}</strong><small>{line.companero_ids?.length ? `Con ${taskCompanions.filter((item) => line.companero_ids.includes(item.id)).map((item) => item.nombre).join(", ")}` : "En solitario"}</small></div><span>{line.cantidad}</span><button type="button" onClick={() => setTaskEditor((current) => ({ ...current, lineas: current.lineas.filter((item) => item.producto_id !== line.producto_id) }))} aria-label={`Quitar ${line.descripcion}`}>×</button></div>) : <p>Aún no has añadido ninguna tarea al parte.</p>}</section>{taskEditor.ai_resultado && <section className="task-ai-result"><strong>Propuesta del asistente</strong>{taskEditor.ai_resultado.resumen && <p>{taskEditor.ai_resultado.resumen}</p>}{(taskEditor.ai_resultado.dudas || []).map((item, index) => <p key={`duda-${index}`}>Duda: {item}</p>)}</section>}{taskFeedback && <p className="modal-message modal-error">{taskFeedback}</p>}<button className="save-button" type="button" disabled={taskSaving || !taskEditor.lineas.length} onClick={saveTaskReport}>{taskSaving ? "Enviando…" : "Enviar parte"}</button></>}</section>}
        </section>
        <nav className="home-navigation" aria-label="Operaciones de campo">{actions.map(({ label, title, icon, key, disabled }) => <button className={`home-navigation-item${disabled ? " home-navigation-item-disabled" : ""}`} key={key} type="button" disabled={disabled} aria-label={title} onClick={key === "hours" ? openHoursReport : key === "tasks" ? openTaskReport : key === "materials" ? openMaterialRequest : key === "receipts" ? openReceipt : undefined}><FieldIcon name={icon} /><span>{label}</span></button>)}</nav>
        <span className="home-version">V{APP_VERSION}</span>
        {taskEditor && taskObra?.ia_partes_activa && <button className="task-voice-fab" type="button" onClick={openTaskVoice} aria-label="Dictar parte con IA"><FieldIcon name="mic" /></button>}
        {taskObraPickerOpen && <Modal sidePanel closing={sidePanelClosing === "task-obra"}><section className="field-modal field-side-panel" role="dialog" aria-modal="true" aria-label="Seleccionar obra"><header className="modal-header"><div><p className="modal-kicker">REPORTAR TAREAS</p><h2>Elige la obra</h2></div><button className="icon-button" type="button" onClick={() => closeSidePanel("task-obra")} aria-label="Cerrar">×</button></header><div className="obra-list">{taskLoading && <p className="modal-message">Cargando obras…</p>}{!taskLoading && !taskFeedback && obras.length === 0 && <p className="modal-message">No tienes obras disponibles.</p>}{!taskLoading && obras.map((obra) => <button className="field-selection-card field-selection-option" key={obra.id} type="button" onClick={() => selectTaskObra(obra)}><strong>{obra.codigo_obra || "Obra"}</strong><span>{obra.oferta_referencia || obra.localizacion || "Sin referencia"}</span><b aria-hidden="true">→</b></button>)}{taskFeedback && <p className="modal-message modal-error">{taskFeedback}</p>}</div></section></Modal>}
        {taskLineDraft && <Modal><section className="field-modal task-line-modal" role="dialog" aria-modal="true" aria-label="Reportar tarea"><header className="modal-header"><div><p className="modal-kicker">REPORTAR TAREA</p><h2>Detalle de tarea</h2></div><button className="icon-button" type="button" onClick={() => setTaskLineDraft(null)} aria-label="Cerrar">×</button></header><strong>{taskLineDraft.product.descripcion}</strong><p className="task-line-available">Material recibido disponible: {taskLineDraft.product.cantidad_disponible_para_reportar}</p><label className="field-label">Cantidad realizada<input type="number" min="0" max={taskLineDraft.product.cantidad_disponible_para_reportar} step="any" inputMode="decimal" value={taskLineDraft.cantidad} onChange={(event) => setTaskLineDraft((current) => ({ ...current, cantidad: event.target.value }))} /></label>{taskCompanions.length > 0 && <section className="task-companions"><strong>Compañeros que han ayudado</strong>{taskCompanions.map((operario) => <button type="button" className={taskLineDraft.companero_ids.includes(operario.id) ? "task-companion-selected" : ""} key={operario.id} onClick={() => setTaskLineDraft((current) => ({ ...current, companero_ids: current.companero_ids.includes(operario.id) ? current.companero_ids.filter((id) => id !== operario.id) : [...current.companero_ids, operario.id] }))}>{operario.nombre}<b>{taskLineDraft.companero_ids.includes(operario.id) ? "✓" : "+"}</b></button>)}</section>}<p className="task-line-helper">{taskLineDraft.companero_ids.length ? `El tiempo se repartirá entre ${taskLineDraft.companero_ids.length + 1} operarios.` : "La tarea se imputará íntegramente a ti."}</p><button className="save-button" type="button" onClick={saveTaskLine}>Añadir al parte</button></section></Modal>}
        {taskVoiceOpen && <Modal card closing={taskVoiceClosing}>
          <section className="field-modal task-voice-modal" role="dialog" aria-modal="true" aria-label="Dictar parte de tareas">
            <header className="modal-header">
              <div><p className="modal-kicker">ASISTENTE DE TAREAS</p><h2>Dictar parte</h2></div>
              <button className="icon-button" type="button" disabled={taskVoiceSending} onClick={closeTaskVoice} aria-label="Cerrar">×</button>
            </header>
            {!taskVoiceRecording && !taskVoiceSending && <p className="task-voice-description">Explica las tareas, cantidades y compañeros. El asistente preparará una propuesta para revisar.</p>}
            {taskVoiceError && <p className="modal-message modal-error">{taskVoiceError}</p>}
            {taskVoiceSending ? <TaskVoiceActivity sending /> : taskVoiceRecording ? <><TaskVoiceActivity /><button className="task-voice-stop" type="button" onClick={() => stopTaskVoiceRecording()}>Finalizar grabación</button></> : <>
              <p className="task-voice-count">{taskVoiceFiles.length ? `${taskVoiceFiles.length} grabación(es) preparada(s)` : "Sin grabaciones todavía"}</p>
              <button className="task-voice-start" type="button" onClick={startTaskVoiceRecording}>Grabar audio</button>
              {taskVoiceFiles.length > 0 && <><button className="save-button" type="button" onClick={sendTaskVoice}>Enviar al asistente</button><button className="task-voice-cancel" type="button" onClick={closeTaskVoice}>Cancelar</button></>}
            </>}
          </section>
        </Modal>}
      </main>
    );
  }

  if (receiptScreen === "receipt") {
    return (
      <main className="field-app home-screen field-screen">
        <header className="home-user-bar"><strong>{session.nombre}</strong></header>
        <section className={`field-workspace receipt-workspace ${receiptViewClosing ? "field-view-exit" : "field-view-enter"}`}>
          <header className="field-view-header"><button className="view-control-button" type="button" onClick={receiptEditor ? () => setReceiptEditor(null) : closeReceipt} aria-label="Volver">←</button><p>RECEPCIÓN DE MATERIAL</p><button className="view-control-button" type="button" onClick={closeReceipt} aria-label="Cerrar">×</button></header>
          <button className="selected-obra-button" type="button" onClick={openReceiptObraPicker}><span>OBRA</span>{receiptObra ? <><strong>{receiptObra.codigo_obra || "Obra"}</strong><small>{receiptObra.oferta_referencia || receiptObra.localizacion || "Sin referencia"}</small></> : <strong className="placeholder">Selecciona una obra</strong>}<b aria-hidden="true">→</b></button>
          {!receiptObra ? null : <section className="receipt-content-panel">{!receiptEditor ? <section className="receipt-orders"><p className="time-title">Pedidos enviados</p>{receiptLoading && <p className="modal-message">Cargando pedidos…</p>}{!receiptLoading && !receiptOrders.filter((order) => order.fecha_envio).length && <p className="receipt-empty-state">No hay pedidos enviados pendientes de recepción.</p>}{receiptOrders.filter((order) => order.fecha_envio).map((order) => <button className="field-selection-card field-selection-option receipt-order-button" key={order.id} type="button" onClick={() => openReceiptEditor(order)}><strong>{order.codigo || "Pedido"}</strong><span>{order.proveedor || "Proveedor"}</span><small>{order.materiales_recibidos || 0} de {order.materiales_solicitados || 0} materiales recibidos</small><b aria-hidden="true">→</b></button>)}</section> : <section className="receipt-editor"><div className="receipt-order-summary"><strong>{receiptEditor.order.codigo || "Pedido"}</strong><span>{receiptObra.codigo_obra || "Obra"}</span></div><label className="field-label">Albarán del proveedor<input value={receiptEditor.albaran_proveedor || ""} onChange={(event) => setReceiptEditor((current) => ({ ...current, albaran_proveedor: event.target.value }))} /></label><button className="capture-button" type="button" onClick={startReceiptCamera}><span aria-hidden="true">▣</span> Hacer foto</button>{receiptFiles.length > 0 && <div className="receipt-files">{receiptFiles.map((item, index) => <div className="receipt-file" key={`${item.file.name}-${index}`}>{item.preview ? <img src={item.preview} alt={`Albarán ${index + 1}`} /> : <div className="receipt-file-pdf">PDF</div>}<span>{item.file.name}</span><button type="button" onClick={() => removeReceiptFile(index)} aria-label={`Quitar ${item.file.name}`}>×</button></div>)}</div>}{receiptFiles.length > 0 && <button className="analyze-button" type="button" disabled={receiptAnalyzing} onClick={analyzeReceiptFiles}>{receiptAnalyzing ? "Analizando albarán…" : `Analizar ${receiptFiles.length} foto${receiptFiles.length === 1 ? "" : "s"}`}</button>}{(receiptEditor.materiales_no_identificados || []).length > 0 && <div className="unmatched-materials"><strong>Materiales no identificados</strong>{receiptEditor.materiales_no_identificados.map((material, index) => <span key={`${material.descripcion}-${index}`}>{material.descripcion}{Number(material.cantidad || 0) > 0 ? ` · ${material.cantidad}` : ""}</span>)}</div>}<section className="receipt-lines"><strong>Material recibido</strong>{receiptEditor.lineas.map((line) => <div className="receipt-line" key={line.id}><div><strong>{line.descripcion_snapshot}</strong><small>{line.referencia_snapshot || ""}{line.unidad_snapshot ? ` · ${line.unidad_snapshot}` : ""}<br />Pendiente: {Math.max(0, Number(line.cantidad || 0) - Number(line.cantidad_recibida || 0))}</small></div><input aria-label={`Cantidad recibida de ${line.descripcion_snapshot}`} inputMode="decimal" value={line.cantidad_actual} onFocus={(event) => event.target.select()} onChange={(event) => updateReceiptQuantity(line.id, event.target.value)} /></div>)}</section>{receiptFeedback && <p className="modal-message modal-error">{receiptFeedback}</p>}<button className="save-button" type="button" disabled={receiptSaving || receiptAnalyzing} onClick={saveReceipt}>{receiptSaving ? "Guardando…" : "Guardar recepción"}</button></section>}</section>}
        </section>
        <nav className="home-navigation" aria-label="Operaciones de campo">{actions.map(({ label, title, icon, key, disabled }) => <button className={`home-navigation-item${disabled ? " home-navigation-item-disabled" : ""}`} key={key} type="button" disabled={disabled} aria-label={title} onClick={key === "hours" ? openHoursReport : key === "tasks" ? openTaskReport : key === "materials" ? openMaterialRequest : key === "receipts" ? openReceipt : undefined}><FieldIcon name={icon} /><span>{label}</span></button>)}</nav>
        <span className="home-version">V{APP_VERSION}</span>
        {cameraOpen && <Modal><section className="field-modal camera-modal" role="dialog" aria-modal="true" aria-label="Hacer foto al albarán"><header className="modal-header"><div><p className="modal-kicker">RECEPCIÓN DE MATERIAL</p><h2>Hacer foto</h2></div><button className="icon-button" type="button" onClick={stopReceiptCamera} aria-label="Cerrar cámara">×</button></header><video className="camera-preview" ref={receiptCameraVideoRef} autoPlay muted playsInline /><div className="camera-actions"><button className="secondary-button" type="button" onClick={stopReceiptCamera}>Cancelar</button><button className="save-button" type="button" onClick={takeReceiptPhoto}>Tomar foto</button></div></section></Modal>}
        {receiptObraPickerOpen && <Modal sidePanel closing={sidePanelClosing === "receipt-obra"}><section className="field-modal field-side-panel" role="dialog" aria-modal="true" aria-label="Seleccionar obra"><header className="modal-header"><div><p className="modal-kicker">RECEPCIÓN DE MATERIAL</p><h2>Elige la obra</h2></div><button className="icon-button" type="button" onClick={() => closeSidePanel("receipt-obra")} aria-label="Cerrar">×</button></header><div className="obra-list">{receiptLoading && <p className="modal-message">Cargando obras…</p>}{!receiptLoading && !receiptFeedback && obras.length === 0 && <p className="modal-message">No tienes obras disponibles.</p>}{!receiptLoading && obras.map((obra) => <button className="field-selection-card field-selection-option" key={obra.id} type="button" onClick={() => selectReceiptObra(obra)}><strong>{obra.codigo_obra || "Obra"}</strong><span>{obra.oferta_referencia || obra.localizacion || "Sin referencia"}</span><b aria-hidden="true">→</b></button>)}{receiptFeedback && <p className="modal-message modal-error">{receiptFeedback}</p>}</div></section></Modal>}
      </main>
    );
  }

  if (materialScreen === "request") {
    return (
      <main className="field-app home-screen field-screen">
        <header className="home-user-bar"><strong>{session.nombre}</strong></header>
        <section className={`field-workspace material-workspace ${materialViewClosing ? "field-view-exit" : "field-view-enter"}`}>
          <header className="field-view-header"><button className="view-control-button" type="button" onClick={closeMaterialRequest} aria-label="Volver">←</button><p>SOLICITAR MATERIAL</p><button className="view-control-button" type="button" onClick={closeMaterialRequest} aria-label="Cerrar">×</button></header>
          <button className="selected-obra-button" type="button" onClick={openMaterialObraPicker}><span>OBRA</span>{materialObra ? <><strong>{materialObra.codigo_obra || "Obra"}</strong><small>{materialObra.oferta_referencia || materialObra.localizacion || "Sin referencia"}</small></> : <strong className="placeholder">Selecciona una obra</strong>}<b aria-hidden="true">→</b></button>
          {!materialObra ? null : <section className="material-request-form"><div className="material-section-heading"><strong>Materiales de la obra</strong><button type="button" className="secondary-button" onClick={() => { setSidePanelClosing(""); setExtraMaterialOpen(true); }}>+ Material adicional</button></div>{materialLoading && <p className="modal-message">Cargando materiales…</p>}{!materialLoading && !(materialEditor.capitulos || []).length && <p className="hours-empty-state">No hay materiales pendientes para solicitar en esta obra.</p>}<div className="material-tree">{(materialEditor.capitulos || []).map((chapter) => { const chapterOpen = expandedChapters.includes(chapter.capitulo_id); return <section className="material-chapter" key={chapter.capitulo_id}><button className="material-chapter-button" type="button" onClick={() => setExpandedChapters((current) => current.includes(chapter.capitulo_id) ? current.filter((id) => id !== chapter.capitulo_id) : [...current, chapter.capitulo_id])}><span>CAP. {chapter.ordinal}</span><strong>{chapter.descripcion}</strong><b>{chapterOpen ? "−" : "+"}</b></button>{chapterOpen && (chapter.partidas || []).map((partida) => { const partidaOpen = expandedPartidas.includes(partida.partida_id); return <div className="material-partida" key={partida.partida_id}><div className="material-partida-heading"><button type="button" onClick={() => setExpandedPartidas((current) => current.includes(partida.partida_id) ? current.filter((id) => id !== partida.partida_id) : [...current, partida.partida_id])}><strong>{partida.ordinal}. {partida.descripcion}</strong><small>{partida.referencia || ""}</small></button><button type="button" className="mini-add-button" onClick={() => addPartidaMaterials(chapter, partida)}>Todo</button></div>{partidaOpen && <div className="material-products">{(partida.productos || []).map((product) => <div className="material-product" key={product.obra_producto_id}><div><strong>{product.descripcion}</strong><small>{product.referencia || ""}{product.unidad ? ` · ${product.unidad}` : ""} · Pendiente: {product.cantidad_pendiente}</small></div><button type="button" className="mini-add-button" onClick={() => addPlannedMaterial(chapter, partida, product)}>Añadir</button></div>)}</div>}</div>; })}</section>; })}</div><section className="selected-materials"><strong>Material seleccionado ({materialSelections.length})</strong>{!materialSelections.length && <p>No has añadido materiales todavía.</p>}{materialSelections.map((line) => <div className="selected-material-line" key={materialKey(line)}><div><strong>{line.descripcion}</strong><small>{line.referencia || ""}{line.unidad ? ` · ${line.unidad}` : ""}</small></div><div><input aria-label={`Cantidad de ${line.descripcion}`} inputMode="decimal" value={line.cantidad_solicitada} onChange={(event) => updateMaterialSelection(line, event.target.value)} /><button type="button" onClick={() => updateMaterialSelection(line, 0)} aria-label={`Quitar ${line.descripcion}`}>×</button></div></div>)}</section><label className="field-label">Observaciones<textarea rows="3" maxLength="1000" value={materialEditor.observaciones || ""} onChange={(event) => setMaterialEditor((current) => ({ ...current, observaciones: event.target.value }))} placeholder="Opcional" /></label>{materialFeedback && <p className="modal-message modal-error">{materialFeedback}</p>}<button className="save-button" type="button" disabled={materialSaving || materialLoading} onClick={saveMaterialRequest}>{materialSaving ? "Guardando…" : "Guardar solicitud"}</button></section>}
        </section>
        <nav className="home-navigation" aria-label="Operaciones de campo">{actions.map(({ label, title, icon, key, disabled }) => <button className={`home-navigation-item${disabled ? " home-navigation-item-disabled" : ""}`} key={key} type="button" disabled={disabled} aria-label={title} onClick={key === "hours" ? openHoursReport : key === "tasks" ? openTaskReport : key === "materials" ? openMaterialRequest : key === "receipts" ? openReceipt : undefined}><FieldIcon name={icon} /><span>{label}</span></button>)}</nav>
        <span className="home-version">V{APP_VERSION}</span>
        {materialObraPickerOpen && <Modal sidePanel closing={sidePanelClosing === "material-obra"}><section className="field-modal field-side-panel" role="dialog" aria-modal="true" aria-label="Seleccionar obra"><header className="modal-header"><div><p className="modal-kicker">SOLICITAR MATERIAL</p><h2>Elige la obra</h2></div><button className="icon-button" type="button" onClick={() => closeSidePanel("material-obra")} aria-label="Cerrar">×</button></header><div className="obra-list">{materialLoading && <p className="modal-message">Cargando obras…</p>}{!materialLoading && !materialFeedback && obras.length === 0 && <p className="modal-message">No tienes obras disponibles.</p>}{!materialLoading && obras.map((obra) => <button className="field-selection-card field-selection-option" key={obra.id} type="button" onClick={() => selectMaterialObra(obra)}><strong>{obra.codigo_obra || "Obra"}</strong><span>{obra.oferta_referencia || obra.localizacion || "Sin referencia"}</span>{obra.oferta_referencia && obra.localizacion && <small>{obra.localizacion}</small>}<b aria-hidden="true">→</b></button>)}{materialFeedback && <p className="modal-message modal-error">{materialFeedback}</p>}</div></section></Modal>}
        {extraMaterialOpen && <Modal sidePanel closing={sidePanelClosing === "extra-material"}><section className="field-modal field-side-panel" role="dialog" aria-modal="true" aria-label="Añadir material adicional"><header className="modal-header"><div><p className="modal-kicker">MATERIAL ADICIONAL</p><h2>Buscar material</h2></div><button className="icon-button" type="button" onClick={() => closeSidePanel("extra-material")} aria-label="Cerrar">×</button></header><label className="field-label">Nombre o referencia<input autoFocus value={extraSearch} onChange={(event) => searchExtraMaterials(event.target.value)} placeholder="Escribe al menos dos caracteres" /></label><div className="obra-list">{extraProducts.map((product) => <button className="field-selection-card field-selection-option material-search-result" key={product.id} type="button" onClick={() => addExtraMaterial(product)}><strong>{product.descripcion}</strong><span>{product.referencia || "Sin referencia"}{product.tipo_unidad?.descripcion ? ` · ${product.tipo_unidad.descripcion}` : ""}</span><b aria-hidden="true">+</b></button>)}</div></section></Modal>}
      </main>
    );
  }

  if (hoursScreen === "report") {
    return (
      <main className="field-app home-screen field-screen">
        <header className="home-user-bar"><strong>{session.nombre}</strong></header>
        <section className={`field-workspace hours-workspace ${hoursViewClosing ? "field-view-exit" : "field-view-enter"}`}>
          <header className="field-view-header"><button className="view-control-button" type="button" onClick={closeHoursReport} aria-label="Volver">←</button><p>REPORTAR HORAS</p><button className="view-control-button" type="button" onClick={closeHoursReport} aria-label="Cerrar">×</button></header>
          <button className="selected-obra-button" type="button" onClick={openObraPicker}><span>OBRA</span>{selectedObra ? <><strong>{selectedObra.codigo_obra || "Obra"}</strong><small>{selectedObra.oferta_referencia || selectedObra.localizacion || "Sin referencia"}</small></> : <strong className="placeholder">Selecciona una obra</strong>}<b aria-hidden="true">→</b></button>
          <button className={`selected-operario-button${selectedObra ? "" : " selected-operario-button-disabled"}`} type="button" disabled={!selectedObra} onClick={openOperarioPicker}><span>OPERARIO</span><strong className={reportingOperario ? "" : "placeholder"}>{reportingOperario?.nombre || "Selecciona un operario"}</strong><b aria-hidden="true">→</b></button>
          {!selectedObra ? null : !reportingOperario ? <p className="hours-empty-state">Elige el operario externo al que corresponden las horas.</p> : <section className="hours-form" aria-label="Parte diario"><input className="hours-date-input" type="date" aria-label="Fecha" value={hoursEntry.fecha} onChange={(event) => changeHoursDate(event.target.value)} /><p className="time-title">Tiempo asignado a esta obra</p>{[{ label: "Horas", unit: "horas", value: hoursEntry.horas, increment: 60 }, { label: "Minutos", unit: "minutos", value: hoursEntry.minutos, increment: 1 }].map((unit) => <div className="duration-row" key={unit.unit}><span>{unit.label}</span><div><button type="button" onClick={() => adjustHours(-unit.increment)} aria-label={`Restar ${unit.label}`}>−</button><input inputMode="numeric" value={String(unit.value).padStart(2, "0")} onFocus={(event) => event.target.select()} onChange={(event) => setHoursUnit(unit.unit, event.target.value)} /><button type="button" onClick={() => adjustHours(unit.increment)} aria-label={`Sumar ${unit.label}`}>+</button></div></div>)}<label className="field-label">Observaciones<textarea rows="3" maxLength="1000" value={hoursEntry.observaciones} onChange={(event) => setHoursEntry((current) => ({ ...current, observaciones: event.target.value }))} placeholder="Opcional" /></label>{loading && <p className="modal-message">Cargando parte…</p>}{feedback && <p className="modal-message modal-error">{feedback}</p>}<button className="save-button" type="button" disabled={saving || loading} onClick={saveHours}>{saving ? "Guardando…" : "Guardar parte"}</button></section>}
        </section>
        <nav className="home-navigation" aria-label="Operaciones de campo">{actions.map(({ label, title, icon, key, disabled }) => <button className={`home-navigation-item${disabled ? " home-navigation-item-disabled" : ""}`} key={key} type="button" disabled={disabled} aria-label={title} onClick={key === "hours" ? openHoursReport : key === "tasks" ? openTaskReport : key === "materials" ? openMaterialRequest : key === "receipts" ? openReceipt : undefined}><FieldIcon name={icon} /><span>{label}</span></button>)}</nav>
        <span className="home-version">V{APP_VERSION}</span>
        {operarioPickerOpen && <Modal sidePanel closing={sidePanelClosing === "operario"}><section className="field-modal field-side-panel" role="dialog" aria-modal="true" aria-label="Seleccionar operario"><header className="modal-header"><div><p className="modal-kicker">REPORTAR HORAS</p><h2>Elige el operario</h2></div><button className="icon-button" type="button" onClick={() => closeSidePanel("operario")} aria-label="Cerrar">×</button></header><div className="obra-list">{loading && <p className="modal-message">Cargando operarios…</p>}{!loading && currentOperarioIsExternal && <p className="modal-message">Solo puedes reportar tus propias horas.</p>}{!loading && !currentOperarioIsExternal && !operariosDisponiblesParaReporte.length && <p className="modal-message">No hay operarios externos disponibles en esta obra.</p>}{!loading && operariosDisponiblesParaReporte.map((operario) => <button className="field-selection-card field-selection-option" key={operario.id} type="button" onClick={() => selectReportingOperario(operario)}><strong>{operario.nombre}</strong><span>{operario.id === session.operarioId ? "Operario actual" : "Operario externo"}</span><b aria-hidden="true">→</b></button>)}</div></section></Modal>}
        {obraPickerOpen && <Modal sidePanel closing={sidePanelClosing === "obra"}><section className="field-modal field-side-panel" role="dialog" aria-modal="true" aria-label="Seleccionar obra"><header className="modal-header"><div><p className="modal-kicker">REPORTAR HORAS</p><h2>Elige la obra</h2></div><button className="icon-button" type="button" onClick={() => closeSidePanel("obra")} aria-label="Cerrar">×</button></header><div className="obra-list">{loading && <p className="modal-message">Cargando obras…</p>}{!loading && !feedback && obras.length === 0 && <p className="modal-message">No tienes obras disponibles.</p>}{!loading && obras.map((obra) => <button className="field-selection-card field-selection-option" key={obra.id} type="button" onClick={() => selectObra(obra)}><strong>{obra.codigo_obra || "Obra"}</strong><span>{obra.oferta_referencia || obra.localizacion || "Sin referencia"}</span>{obra.oferta_referencia && obra.localizacion && <small>{obra.localizacion}</small>}<b aria-hidden="true">→</b></button>)}{feedback && <p className="modal-message modal-error">{feedback}</p>}</div></section></Modal>}
      </main>
    );
  }

  return (
    <main className="field-app home-screen">
      <header className="home-user-bar"><strong>{session.nombre}</strong></header>
      <section className="home-content" aria-label="Inicio de Field"><img className="home-brand-logo" src="/media/logos/neo4-white.svg" alt="NEO4" />
        {lastHoursReport && <section className="hours-report-status home-hours-report" aria-live="polite"><p>REPORTE DE HORAS</p><strong>{lastHoursReport.operario?.nombre || session.nombre}</strong><span>{formatDuration(lastHoursReport.totalMinutes)} · {lastHoursReport.obra.codigo_obra || "Obra"}</span></section>}
      </section>
      <nav className="home-navigation" aria-label="Operaciones de campo">
        {actions.map(({ label, title, icon, key, disabled }) => <button className={`home-navigation-item${disabled ? " home-navigation-item-disabled" : ""}`} key={key} type="button" disabled={disabled} aria-label={title} onClick={key === "hours" ? openHoursReport : key === "tasks" ? openTaskReport : key === "materials" ? openMaterialRequest : key === "receipts" ? openReceipt : undefined}><FieldIcon name={icon} /><span>{label}</span></button>)}
      </nav>
      <span className="home-version">V{APP_VERSION}</span>
      {notification && <div className="field-toast" role="status"><p>REPORTE DE HORAS</p><strong>{notification}</strong></div>}
    </main>
  );
}

ReactDOM.render(<App />, document.getElementById("root"));
